// src/pages/subscription/SubscriptionDashboardPage.tsx

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import {
  useSubscription,
  useSubscriptionAddon,
  useSubscriptionBundle,
  useSubscriptionPlan,
  useI18n,
} from '@react/hooks';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  ErrorAlert,
} from '@react/components';

function PlansIcon() {
  return (
    <svg className="h-6 w-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
    </svg>
  );
}

function SubscriptionsIcon() {
  return (
    <svg className="h-6 w-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  );
}

function BundlesIcon() {
  return (
    <svg className="h-6 w-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  );
}

function AddOnsIcon() {
  return (
    <svg className="h-6 w-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

const QUICK_LINKS = [
  { label: 'Plans', path: '/subscriptions/plans', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01' },
  { label: 'Subscriptions', path: '/subscriptions', icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15' },
  { label: 'Discounts', path: '/subscriptions/discounts', icon: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z' },
  { label: 'Add-Ons', path: '/subscriptions/addons', icon: 'M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z' },
  { label: 'Bundles', path: '/subscriptions/bundles', icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4' },
  { label: 'Entitlements', path: '/subscriptions/entitlements', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
  { label: 'Analytics', path: '/subscriptions/analytics', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
  { label: 'Audit Log', path: '/subscriptions/audit-log', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
  { label: 'Settings', path: '/subscriptions/settings', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' },
];

export const SubscriptionDashboardPage = observer(() => {
  const store = useSubscription();
  const planStore = useSubscriptionPlan();
  const bundleStore = useSubscriptionBundle();
  const addonStore = useSubscriptionAddon();
  const { t } = useI18n();
  const [stats, setStats] = useState({ plans: 0, subscriptions: 0, bundles: 0, addons: 0 });

  useEffect(() => {
    const params = { pagination: { limit: 100, cursor: '', direction: 'forward' }, sort: [], date_range: {} as any, filters: {} as any };
    Promise.allSettled([
      planStore.ListPlans(params),
      store.ListSubscriptions(params),
      bundleStore.ListProductBundles(params),
      addonStore.ListAddOns(params),
    ]).then(() => {
      setStats({
        plans: planStore.items.length,
        subscriptions: store.items.length,
        bundles: bundleStore.items.length,
        addons: addonStore.items.length,
      });
    });
  }, []);

  const error = store.error ?? planStore.error ?? bundleStore.error ?? addonStore.error;
  const clearErrors = () => {
    store.clearError();
    planStore.clearError();
    bundleStore.clearError();
    addonStore.clearError();
  };

  return (
    <div className="space-y-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <h1 className="sr-only">{t('subscription.dashboard')}</h1>

      {error ? <ErrorAlert error={error} dismissible onDismiss={clearErrors} /> : null}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 rounded-lg bg-primary/10 p-3"><PlansIcon /></div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stats.plans}</p>
                <p className="text-sm text-muted-foreground">{t('subscription.plans')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 rounded-lg bg-primary/10 p-3"><SubscriptionsIcon /></div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stats.subscriptions}</p>
                <p className="text-sm text-muted-foreground">{t('subscription.activeSubscriptions')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 rounded-lg bg-primary/10 p-3"><BundlesIcon /></div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stats.bundles}</p>
                <p className="text-sm text-muted-foreground">{t('subscription.bundles')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 rounded-lg bg-primary/10 p-3"><AddOnsIcon /></div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stats.addons}</p>
                <p className="text-sm text-muted-foreground">{t('subscription.addOns')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick links */}
      <Card>
        <CardHeader>
          <CardTitle>{t('common.navigation')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {QUICK_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted transition-colors group"
              >
                <div className="flex-shrink-0 rounded-md bg-primary/10 p-2 group-hover:bg-primary/20 transition-colors">
                  <svg className="h-5 w-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={link.icon} />
                  </svg>
                </div>
                <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                  {link.label}
                </span>
                <svg className="ml-auto h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
});

export default SubscriptionDashboardPage;
